import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Save, LogOut, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  adminListArtworks,
  adminCreateArtwork,
  adminUpdateArtwork,
  adminDeleteArtwork,
  adminListPress,
  adminCreatePress,
  adminUpdatePress,
  adminDeletePress,
} from "@/lib/api";

const EMPTY_ARTWORK = {
  title: "",
  year: new Date().getFullYear(),
  medium: "",
  dimensions: "",
  category: "Paintings",
  description: "",
  image_url: "",
  video_url: "",
  availability: "Available",
  featured: false,
  order: 100,
};

const EMPTY_PRESS = {
  publication: "",
  headline: "",
  date: "",
  description: "",
  thumbnail: "",
  source_url: "",
  quote: "",
  type: "Publication",
  verified: true,
  order: 100,
};

export default function AdminDashboard() {
  const { user, signOut } = useAuth();
  const [tab, setTab] = useState("artworks");

  return (
    <section className="pt-10 pb-24">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
        <div className="flex items-end justify-between gap-6 pb-6 border-b border-[hsl(var(--line))]">
          <div>
            <p className="catalog-label mb-2">Studio admin</p>
            <h1 className="font-serif text-4xl sm:text-5xl">
              Editing everything.
            </h1>
            <p className="mt-2 text-sm text-[hsl(var(--ink-muted))]">
              Signed in as <span className="font-mono">{user?.email}</span>
            </p>
          </div>
          <button
            onClick={signOut}
            data-testid="admin-signout"
            className="inline-flex items-center gap-2 border border-[hsl(var(--ink))] px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="mt-6 flex gap-2 border-b border-[hsl(var(--line))]">
          {[
            { id: "artworks", label: "Artwork" },
            { id: "press", label: "Press & Media" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-5 py-3 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-[hsl(var(--vermillion))] text-[hsl(var(--vermillion))]"
                  : "border-transparent text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink))]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "artworks" ? <ArtworksAdmin /> : <PressAdmin />}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------
// Artworks
// -----------------------------------------------------------------
function ArtworksAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // artwork object or "new"

  const reload = () =>
    adminListArtworks()
      .then(setRows)
      .catch(() => toast.error("Could not load artworks"));

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, []);

  const onDelete = async (a) => {
    if (!confirm(`Delete "${a.title}"? This can't be undone.`)) return;
    try {
      await adminDeleteArtwork(a.id);
      toast.success("Deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onSaved = () => {
    setEditing(null);
    reload();
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-6">
        <p className="catalog-label">{rows.length} works</p>
        <button
          onClick={() => setEditing({ ...EMPTY_ARTWORK, __new: true })}
          data-testid="admin-add-artwork"
          className="inline-flex items-center gap-2 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--vermillion))] transition-colors"
        >
          <Plus size={14} /> New artwork
        </button>
      </div>

      {loading ? (
        <p className="catalog-label">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-artworks-grid">
          {rows.map((a) => (
            <div key={a.id} className="border border-[hsl(var(--line))] bg-[hsl(var(--surface))] p-4 flex gap-4">
              <div className="w-24 h-32 bg-[hsl(var(--bg-secondary))] flex items-center justify-center overflow-hidden flex-shrink-0">
                {a.image_url && (
                  <img
                    src={a.image_url}
                    alt={a.title}
                    className="max-w-full max-h-full object-contain"
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="catalog-label">{a.category} · {a.year}</p>
                <h3 className="font-serif text-lg leading-tight truncate">{a.title}</h3>
                <p className="text-xs text-[hsl(var(--ink-muted))] mt-1 truncate">
                  {a.medium}
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => setEditing(a)}
                    data-testid={`admin-edit-artwork-${a.id}`}
                    className="text-xs font-mono uppercase tracking-widest px-3 py-1 border border-[hsl(var(--line))] hover:border-[hsl(var(--ink))] transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(a)}
                    data-testid={`admin-delete-artwork-${a.id}`}
                    className="text-xs font-mono uppercase tracking-widest px-3 py-1 border border-[hsl(var(--line))] hover:border-[hsl(var(--vermillion))] hover:text-[hsl(var(--vermillion))] transition-colors inline-flex items-center gap-1"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ArtworkEditor artwork={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
    </div>
  );
}

function ArtworkEditor({ artwork, onClose, onSaved }) {
  const [form, setForm] = useState(artwork);
  const [busy, setBusy] = useState(false);
  const isNew = !!artwork.__new;
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        year: Number(form.year),
        medium: form.medium,
        dimensions: form.dimensions,
        category: form.category,
        description: form.description,
        image_url: form.image_url,
        video_url: form.video_url || null,
        availability: form.availability,
        featured: !!form.featured,
        order: Number(form.order) || 0,
      };
      if (isNew) await adminCreateArtwork(payload);
      else await adminUpdateArtwork(artwork.id, payload);
      toast.success(isNew ? "Artwork added" : "Artwork updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      data-testid="admin-artwork-editor"
    >
      <form
        onSubmit={onSave}
        className="w-full max-w-3xl bg-[hsl(var(--bg))] border border-[hsl(var(--line))] p-6 sm:p-10"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="catalog-label">{isNew ? "New" : "Editing"}</p>
            <h2 className="font-serif text-3xl">
              {isNew ? "New artwork" : form.title}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2" data-testid="admin-artwork-editor-close">
            <X size={20} />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <F label="Title" required>
            <input required value={form.title} onChange={set("title")} className="input" data-testid="admin-field-title" />
          </F>
          <F label="Year" required>
            <input type="number" required value={form.year} onChange={set("year")} className="input" data-testid="admin-field-year" />
          </F>
          <F label="Category" required>
            <input required value={form.category} onChange={set("category")} className="input" data-testid="admin-field-category" placeholder="Paintings / Drawings / Portraits / Ambidextrous Works / Earlier Work / Abstract" />
          </F>
          <F label="Medium" required>
            <input required value={form.medium} onChange={set("medium")} className="input" data-testid="admin-field-medium" />
          </F>
          <F label="Dimensions" required>
            <input required value={form.dimensions} onChange={set("dimensions")} className="input" data-testid="admin-field-dimensions" placeholder='e.g. 24 × 30 in' />
          </F>
          <F label="Availability">
            <select value={form.availability} onChange={set("availability")} className="input" data-testid="admin-field-availability">
              <option>Available</option>
              <option>Sold</option>
              <option>Inquire</option>
              <option>Commission</option>
              <option>Not for sale</option>
            </select>
          </F>
          <F label="Image URL" required>
            <input required value={form.image_url} onChange={set("image_url")} className="input" data-testid="admin-field-image" placeholder="https://…" />
          </F>
          <F label="Video URL (optional)">
            <input value={form.video_url || ""} onChange={set("video_url")} className="input" data-testid="admin-field-video" placeholder="https://…" />
          </F>
          <F label="Sort order">
            <input type="number" value={form.order} onChange={set("order")} className="input" data-testid="admin-field-order" />
          </F>
          <F label="Featured">
            <label className="inline-flex items-center gap-2 h-11">
              <input type="checkbox" checked={!!form.featured} onChange={set("featured")} className="w-4 h-4 accent-[hsl(var(--vermillion))]" data-testid="admin-field-featured" />
              <span className="text-sm">Show on homepage</span>
            </label>
          </F>
        </div>
        <F label="Description" required>
          <textarea required rows={5} value={form.description} onChange={set("description")} className="input resize-y" data-testid="admin-field-description" />
        </F>

        {form.image_url && (
          <div className="mt-6">
            <p className="catalog-label mb-2">Preview</p>
            <div className="w-40 h-52 bg-[hsl(var(--bg-secondary))] flex items-center justify-center overflow-hidden">
              <img src={form.image_url} alt="preview" className="max-w-full max-h-full object-contain" />
            </div>
          </div>
        )}

        <div className="mt-8 flex items-center gap-3">
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-artwork-save"
            className="inline-flex items-center gap-2 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--vermillion))] transition-colors disabled:opacity-50"
          >
            <Save size={14} /> {busy ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} className="px-6 py-3 font-mono text-xs uppercase tracking-widest border border-[hsl(var(--line))] hover:border-[hsl(var(--ink))]">
            Cancel
          </button>
        </div>
      </form>

      <style>{`
        .input {
          width: 100%;
          padding: 10px 12px;
          background: hsl(var(--surface));
          border: 1px solid hsl(var(--line));
          border-radius: 2px;
          font-size: 14px;
          color: hsl(var(--ink));
          transition: border-color 200ms ease;
        }
        .input:focus { outline: none; border-color: hsl(var(--ink)); }
      `}</style>
    </div>
  );
}

// -----------------------------------------------------------------
// Press
// -----------------------------------------------------------------
function PressAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = () =>
    adminListPress()
      .then(setRows)
      .catch(() => toast.error("Could not load press"));

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, []);

  const onDelete = async (p) => {
    if (!confirm(`Delete "${p.headline}"?`)) return;
    try {
      await adminDeletePress(p.id);
      toast.success("Deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onSaved = () => {
    setEditing(null);
    reload();
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-6">
        <p className="catalog-label">{rows.length} entries</p>
        <button
          onClick={() => setEditing({ ...EMPTY_PRESS, __new: true })}
          data-testid="admin-add-press"
          className="inline-flex items-center gap-2 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--vermillion))] transition-colors"
        >
          <Plus size={14} /> New press item
        </button>
      </div>

      {loading ? (
        <p className="catalog-label">Loading…</p>
      ) : (
        <div className="space-y-3" data-testid="admin-press-list">
          {rows.map((p) => (
            <div key={p.id} className="border border-[hsl(var(--line))] bg-[hsl(var(--surface))] p-4 flex gap-4 items-start">
              <div className="min-w-0 flex-1">
                <p className="catalog-label">
                  {p.publication} · {p.type} {p.date && `· ${p.date}`}
                  {!p.verified && (
                    <span className="ml-2 text-[hsl(var(--vermillion))]">unverified</span>
                  )}
                </p>
                <h3 className="font-serif text-lg leading-tight">{p.headline}</h3>
                {p.source_url && (
                  <p className="text-xs text-[hsl(var(--ink-muted))] mt-1 truncate">{p.source_url}</p>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setEditing(p)}
                  data-testid={`admin-edit-press-${p.id}`}
                  className="text-xs font-mono uppercase tracking-widest px-3 py-1 border border-[hsl(var(--line))] hover:border-[hsl(var(--ink))]"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(p)}
                  data-testid={`admin-delete-press-${p.id}`}
                  className="text-xs font-mono uppercase tracking-widest px-3 py-1 border border-[hsl(var(--line))] hover:border-[hsl(var(--vermillion))] hover:text-[hsl(var(--vermillion))] inline-flex items-center gap-1"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PressEditor item={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
    </div>
  );
}

function PressEditor({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [busy, setBusy] = useState(false);
  const isNew = !!item.__new;
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        publication: form.publication,
        headline: form.headline,
        date: form.date || "",
        description: form.description || "",
        thumbnail: form.thumbnail || null,
        source_url: form.source_url || null,
        quote: form.quote || null,
        type: form.type || "Publication",
        verified: !!form.verified,
        order: Number(form.order) || 0,
      };
      if (isNew) await adminCreatePress(payload);
      else await adminUpdatePress(item.id, payload);
      toast.success(isNew ? "Press item added" : "Press item updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-testid="admin-press-editor">
      <form onSubmit={onSave} className="w-full max-w-3xl bg-[hsl(var(--bg))] border border-[hsl(var(--line))] p-6 sm:p-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="catalog-label">{isNew ? "New" : "Editing"}</p>
            <h2 className="font-serif text-3xl">{isNew ? "New press item" : form.headline}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2" data-testid="admin-press-editor-close">
            <X size={20} />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <F label="Publication" required>
            <input required value={form.publication} onChange={set("publication")} className="input" data-testid="admin-field-publication" />
          </F>
          <F label="Type">
            <select value={form.type} onChange={set("type")} className="input" data-testid="admin-field-type">
              <option>Television</option>
              <option>Publication</option>
              <option>Interview</option>
              <option>Book</option>
              <option>Social</option>
              <option>Other</option>
            </select>
          </F>
          <F label="Date">
            <input value={form.date || ""} onChange={set("date")} className="input" placeholder="YYYY-MM-DD" data-testid="admin-field-date" />
          </F>
          <F label="Source URL">
            <input value={form.source_url || ""} onChange={set("source_url")} className="input" data-testid="admin-field-source-url" placeholder="https://…" />
          </F>
          <F label="Thumbnail URL">
            <input value={form.thumbnail || ""} onChange={set("thumbnail")} className="input" data-testid="admin-field-thumbnail" placeholder="https://…" />
          </F>
          <F label="Sort order">
            <input type="number" value={form.order} onChange={set("order")} className="input" data-testid="admin-field-press-order" />
          </F>
          <F label="Verified">
            <label className="inline-flex items-center gap-2 h-11">
              <input type="checkbox" checked={!!form.verified} onChange={set("verified")} className="w-4 h-4 accent-[hsl(var(--vermillion))]" data-testid="admin-field-verified" />
              <span className="text-sm">Show on public press page</span>
            </label>
          </F>
        </div>
        <F label="Headline" required>
          <input required value={form.headline} onChange={set("headline")} className="input" data-testid="admin-field-headline" />
        </F>
        <F label="Description">
          <textarea rows={3} value={form.description || ""} onChange={set("description")} className="input resize-y" data-testid="admin-field-press-description" />
        </F>
        <F label="Pull quote">
          <input value={form.quote || ""} onChange={set("quote")} className="input" data-testid="admin-field-quote" />
        </F>

        <div className="mt-8 flex items-center gap-3">
          <button type="submit" disabled={busy} data-testid="admin-press-save" className="inline-flex items-center gap-2 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--vermillion))] transition-colors disabled:opacity-50">
            <Save size={14} /> {busy ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} className="px-6 py-3 font-mono text-xs uppercase tracking-widest border border-[hsl(var(--line))] hover:border-[hsl(var(--ink))]">
            Cancel
          </button>
        </div>
      </form>

      <style>{`
        .input {
          width: 100%; padding: 10px 12px;
          background: hsl(var(--surface));
          border: 1px solid hsl(var(--line));
          border-radius: 2px; font-size: 14px; color: hsl(var(--ink));
        }
        .input:focus { outline: none; border-color: hsl(var(--ink)); }
      `}</style>
    </div>
  );
}

function F({ label, required, children }) {
  return (
    <label className="block mt-6 first:mt-0">
      <span className="catalog-label mb-2 block">
        {label}
        {required && <span className="text-[hsl(var(--vermillion))]"> *</span>}
      </span>
      {children}
    </label>
  );
}
